import React, { useEffect, useState } from "react";
import Tag from "../../../../ui-components/molecules/tag/tag";
import {
  InboundServiceRequestDetail,
  InboundServiceRequestListItem,
  PickupReviewStatus,
} from "../../../../app/data/communications/models";
import {
  REVIEW_STATUS_COLORS,
  REVIEW_STATUS_LABELS,
  formatRequestType,
} from "../constants";
import CapturedBolForm from "./capturedBolForm";
import DuplicatePickupCheckPanel from "./duplicatePickupCheckPanel";
import AiConfidenceBar from "./aiConfidenceBar";

type ServiceRequest = InboundServiceRequestListItem | InboundServiceRequestDetail;

export interface ServiceRequestCollapsibleProps {
  serviceRequest: ServiceRequest;
  defaultExpanded?: boolean;
  senderEmail?: string;
  onPickupNumberClick?: (pickupNumber: string) => void;
  onReviewComplete?: () => void;
}

const ServiceRequestCollapsible: React.FC<ServiceRequestCollapsibleProps> = ({
  serviceRequest,
  defaultExpanded = false,
  senderEmail,
  onPickupNumberClick,
  onReviewComplete,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [requesterTeamId, setRequesterTeamId] = useState<string | null>(null);
  const pickup = serviceRequest.pickupRequest;
  const confidence = pickup?.confidenceScore ?? serviceRequest.confidenceScore;
  const reviewStatus = pickup?.reviewStatus;
  const reviewReason = serviceRequest.reviewReason ?? pickup?.reviewReason;
  const isPendingReview = reviewStatus === "REQUIRE_HUMAN_REVIEW";

  useEffect(() => {
    setRequesterTeamId(null);
  }, [pickup?.id]);

  return (
    <div className="xgs-pickup-inbox__service-request">
      <div
        role="button"
        tabIndex={0}
        className="xgs-pickup-inbox__service-request__header"
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setExpanded(!expanded);
          }
        }}
        aria-expanded={expanded}
      >
        <div className="xgs-pickup-inbox__service-request__left">
          <span className="xgs-pickup-inbox__service-request__type">
            {formatRequestType(serviceRequest.requestType)}
          </span>
          {pickup?.oneXgsPickupNumber && (
            <span
              role="link"
              tabIndex={0}
              className="xgs-pickup-inbox__service-request__pickup-link"
              onClick={(e) => {
                e.stopPropagation();
                onPickupNumberClick?.(pickup.oneXgsPickupNumber!);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  onPickupNumberClick?.(pickup.oneXgsPickupNumber!);
                }
              }}
            >
              {pickup.oneXgsPickupNumber}
            </span>
          )}
          <AiConfidenceBar score={confidence} className="xgs-pickup-inbox__service-request__confidence-bar" />
        </div>
        {reviewStatus && (
          <span className="xgs-pickup-inbox__service-request__status">
            <Tag mods={{ color: REVIEW_STATUS_COLORS[reviewStatus as PickupReviewStatus] }}>
              {REVIEW_STATUS_LABELS[reviewStatus as PickupReviewStatus] ?? reviewStatus}
            </Tag>
          </span>
        )}
      </div>
      {expanded && (
        <div className="xgs-pickup-inbox__service-request__body">
          {reviewReason && (
            <div className="xgs-pickup-inbox__service-request__reason">
              <strong>Review reason:</strong> {reviewReason}
            </div>
          )}
          {isPendingReview && pickup?.id && (
            <DuplicatePickupCheckPanel
              pickupRequestId={pickup.id}
              teamId={requesterTeamId}
              senderEmail={senderEmail}
              excludePickupNumber={pickup.oneXgsPickupNumber}
              onPickupNumberClick={onPickupNumberClick}
              onInitialTeamIdResolved={setRequesterTeamId}
            />
          )}
          {pickup?.id && reviewStatus && (
            <CapturedBolForm
              pickupRequestId={pickup.id}
              reviewStatus={reviewStatus as PickupReviewStatus}
              confidenceScore={confidence}
              pickupNumber={pickup.oneXgsPickupNumber}
              senderEmail={senderEmail}
              onReviewComplete={onReviewComplete}
              onRequesterTeamIdChange={setRequesterTeamId}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ServiceRequestCollapsible;
